import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Btn, Field, inputCls } from '../../components/platform/ui';
import { platformAdminApi, errorText } from '../../services/platformApi';
import VideoPlayer from '../../components/platform/VideoPlayer';

export const ImageField = ({ label, value, onChange }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await platformAdminApi.uploadImage(file);
      onChange(url);
      toast.success(t('pl.admin.shared.imageUploaded'));
    } catch (err) { toast.error(errorText(err, t('pl.admin.shared.imageFail'))); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <Field label={label || t('pl.admin.shared.image')} hint={t('pl.admin.shared.imageHint')}>
      <div className="flex gap-2">
        <input className={inputCls} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={t('pl.admin.shared.urlPlaceholder')} />
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} />
        <Btn variant="secondary" loading={busy} onClick={() => ref.current?.click()}>{t('pl.admin.shared.upload')}</Btn>
      </div>
      {value && <img src={value} alt="" className="mt-2 h-24 border border-black object-cover" />}
    </Field>
  );
};

const MAX_VIDEO_MB = 100; // лимит Cloudflare на загрузку

/** Видео: ссылка (YouTube, VK, RuTube, Vimeo) или загрузка файла MP4/WebM. */
export const VideoField = ({ label, value, aspect, onChange, onAspect, poster }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) { toast.error(t('pl.admin.shared.videoTooBig', { mb: MAX_VIDEO_MB })); if (ref.current) ref.current.value = ''; return; }
    setBusy(true); setProgress(0);
    try {
      const { url } = await platformAdminApi.uploadVideo(file, setProgress);
      onChange(url);
      toast.success(t('pl.admin.shared.videoUploaded'));
    } catch (err) { toast.error(errorText(err, t('pl.admin.shared.videoFail'))); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const isFile = (value || '').startsWith('/api/media/');
  const handleLink = (v) => {
    onChange(v);
    // подсказываем вертикальный формат для Shorts / Reels
    if (/\/shorts\/|\/reel\//i.test(v) && onAspect) onAspect('9:16');
  };
  return (
    <Field label={label || t('pl.admin.shared.video')} hint={t('pl.admin.shared.videoHint', { mb: MAX_VIDEO_MB })}>
      <div className="flex gap-2">
        <input className={inputCls} value={value || ''} onChange={(e) => handleLink(e.target.value)} placeholder={t('pl.admin.shared.urlPlaceholder')} />
        <input ref={ref} type="file" accept="video/mp4,video/webm" className="hidden" onChange={upload} />
        <Btn variant="secondary" loading={busy} onClick={() => ref.current?.click()}>{busy ? `${progress}%` : t('pl.admin.shared.upload')}</Btn>
      </div>
      {busy && <div className="h-2 bg-gray-200 mt-2"><div className="h-2 bg-[#C41E3A]" style={{ width: `${progress}%` }} /></div>}
      {value && (
        <div className="mt-3 max-w-md">
          <VideoPlayer url={value} aspect={aspect || '16:9'} poster={poster} />
          <button type="button" className="text-sm underline mt-2" onClick={() => onChange('')}>{t('pl.admin.shared.removeVideo')}</button>
        </div>
      )}
      {value && !isFile && onAspect && (
        <div className="mt-3">
          <span className="block text-sm font-bold mb-1">{t('pl.admin.shared.aspect')}</span>
          <select className={inputCls} value={aspect || '16:9'} onChange={(e) => onAspect(e.target.value)}>
            <option value="16:9">{t('pl.admin.shared.aspect169')}</option>
            <option value="9:16">{t('pl.admin.shared.aspect916')}</option>
            <option value="1:1">{t('pl.admin.shared.aspect11')}</option>
            <option value="4:5">{t('pl.admin.shared.aspect45')}</option>
          </select>
        </div>
      )}
      {value && isFile && <p className="text-xs text-gray-500 mt-2">{t('pl.admin.shared.fileProportions')}</p>}
    </Field>
  );
};

export const Text = ({ label, value, onChange, hint, ...rest }) => (
  <Field label={label} hint={hint}><input className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} /></Field>
);
export const Area = ({ label, value, onChange, rows = 4, hint, ...rest }) => (
  <Field label={label} hint={hint}><textarea className={inputCls} rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest} /></Field>
);
export const Select = ({ label, value, onChange, options, hint }) => (
  <Field label={label} hint={hint}>
    <select className={inputCls} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </Field>
);
export const Check = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 mb-4"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />{label}</label>
);

export const lines = (s) => (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
export const pairs = (s, a, b) => lines(s).map((l) => { const [x, ...r] = l.split('|'); return { [a]: x.trim(), [b]: r.join('|').trim() }; });
export const unpairs = (arr, a, b) => (arr || []).map((x) => `${x[a] ?? ''} | ${x[b] ?? ''}`).join('\n');

// ---- Английские переводы контента (поле translations.en) ----
// spec: [{ key, label, kind: 'text' | 'area' | 'lines' | 'pairs', a, b, rows, hint }]
const enToText = (sp, v) => {
  if (v == null) return '';
  if (sp.kind === 'lines') return (v || []).join('\n');
  if (sp.kind === 'pairs') return unpairs(v, sp.a, sp.b);
  return String(v);
};
export const initEn = (spec, translations) => Object.fromEntries(spec.map((sp) => [sp.key, enToText(sp, translations?.en?.[sp.key])]));
export const buildTranslations = (spec, state) => {
  const en = {};
  spec.forEach((sp) => {
    const raw = state[sp.key] || '';
    if (!raw.trim()) return;
    if (sp.kind === 'lines') en[sp.key] = lines(raw);
    else if (sp.kind === 'pairs') en[sp.key] = pairs(raw, sp.a, sp.b);
    else en[sp.key] = raw;
  });
  return Object.keys(en).length ? { en } : {};
};
export const EnTranslations = ({ spec, value, onChange }) => {
  const { t } = useTranslation();
  return (
    <details className="border-2 border-black bg-white p-3 mb-4">
      <summary className="cursor-pointer font-semibold">{t('pl.admin.shared.enSummary')}</summary>
      <p className="text-sm text-gray-600 my-2">{t('pl.admin.shared.enNote')}</p>
      {spec.map((sp) => {
        const set = (v) => onChange({ ...value, [sp.key]: v });
        const hint = sp.hint || (sp.kind === 'lines' ? t('pl.admin.shared.enLines') : sp.kind === 'pairs' ? t('pl.admin.shared.enPairs', { a: sp.a, b: sp.b }) : undefined);
        return sp.kind === 'text'
          ? <Text key={sp.key} label={sp.label} value={value[sp.key]} onChange={set} hint={hint} />
          : <Area key={sp.key} label={sp.label} rows={sp.rows || 3} value={value[sp.key]} onChange={set} hint={hint} />;
      })}
    </details>
  );
};

export const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

export const DataTable = ({ columns, rows, empty, rowKey = 'id' }) => {
  const { t } = useTranslation();
  return (
  <div className="overflow-x-auto border-2 border-black bg-[#F5F1E8]">
    <table className="w-full text-left text-sm">
      <thead className="bg-black text-white"><tr>{columns.map((c) => <th key={c.label} className="p-2 whitespace-nowrap">{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={columns.length} className="p-4 text-gray-600">{empty || t('pl.common.noData')}</td></tr>}
        {rows.map((r) => (
          <tr key={r[rowKey]} className="border-t border-black/20 align-top">
            {columns.map((c) => <td key={c.label} className="p-2">{c.render ? c.render(r) : r[c.key]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};
